import React, { useEffect, useId, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { lionAutoKeys, useLionAutoCheckerPreview, useImportLionAutoChecker } from '../../hooks/useLionAuto';
import {
  defaultSelection, toggleSelection, selectableKeys, allSelected, importSelections, sectionsText, rowsText,
  qtyText, methodText, actionLabel, noteLines, confirmImportText, checkerErrorText,
  checkerPreviewRetryable, importNeedsReload, importResultText, orphanText, oursText, visibleProposals,
  includeEmptyText, emptyShownText, specificityText, proposalKey, filterLabelText, joinedShows, showLinesText,
} from '../../utils/lionAutoChecker';

// Pricing groups → "Import from Lion Checker": the groups this show's Lion
// Checker filters would create or update (the server's preview, no write),
// one row per filter. Creates and updates of groups off Auto start ticked;
// an update of a group on Auto is opt-in and confirms first. Import sends
// the ticked filters with their signatures; the server recomputes them and
// answers 409 when one changed on Lion Checker since this preview (Reload
// preview). Imported groups land in the list at once (useImportLionAutoChecker).
// Ours: how many of our listings each filter holds today, each listing
// counted for its most specific filter only. A new filter that holds none is
// left out (the server skips it NO_LISTINGS or OUTRANKED) until the pricer
// ticks "Include N filters…": the preview re-reads with includeEmpty, they
// come back as New (unticked), and the import sends includeEmpty too. While
// it re-reads, the last preview stays on screen under its own includeEmpty;
// when that re-read fails, the box stays to untick back to it.
// Moves: importing a filter moves the listings it wins out of the less
// specific groups that hold them. A create or update that would take any
// says so in its Note column ("takes 4 from Checker 1 · 101-110 (3
// ranked)"). The import confirms first only when Auto is involved (a
// ticked update on Auto, or ranked listings leaving a group on Auto); the
// confirm lists the ticked filters' moves by group name.
// Joined shows: every Ticketmaster show on Lion Checker with this event's
// Vivid link (the club-seats event beside the main one) comes in one
// preview, a line each at the top (a twin's tag first, "Club · …"). Rows run
// show by show; a twin's filter reads "Club 3" in the # column. A row and
// its tick are keyed by show + filter (proposalKey), as two shows can both
// have a filter 3.
// Every text from Lion Checker or Skybox renders as text.
//
// Props: eventId; vividUrl (the page's VividSeats link, a fallback when
// Skybox has none, or null); groups (the event's saved groups: their current
// names); onClose().

const HINT = 'Filters are matched by their position on Lion Checker. '
  + 'Each of our listings counts for its most specific filter (rows, then min seats, then sections), '
  + 'and importing that filter moves it there from a less specific group: a ranked one lands at its Smart sort spot '
  + "and keeps its price (its old group's ranks close up), an unranked one joins a new group's ranking "
  + "or an updated group's Needs approval. "
  + 'A listing stays where it is when a group includes it by hand, or its group has a notes filter '
  + 'or is at least as specific. '
  + 'Filters that hold none are left out unless you include them.';
// The box after a failed re-read: no preview, so no count
const INCLUDE_EMPTY_LABEL = 'Include the filters that hold none of our listings';
// The Ours cell's title; a newer server adds how specific the filter is
const OURS_TITLE = 'Our listings this filter holds today · seats';
const OURS_WINS_TITLE = 'Our listings this filter wins today · seats';

const ACTION_TITLES = {
  create: 'Creates a new group from this filter (Auto off)',
  update: 'Updates the group made from this filter: its sections, rows and Qty (its name, floor and Auto stay); '
    + 'listings it wins move in from less specific groups',
  unchanged: 'The group made from this filter already matches it',
  skipped: 'Not imported: see the note',
};

function ProposalRow({ p, checked, canTick, locked, onToggle, nameOf }) {
  const chip = actionLabel(p);
  const sections = Array.isArray(p.derived?.sections) ? p.derived.sections : [];
  const rows = Array.isArray(p.derived?.rowsInclude) ? p.derived.rowsInclude : [];
  const unresolved = (Array.isArray(p.resolution) ? p.resolution : [])
    .filter(r => r?.method === 'unresolved')
    .map(r => r.token);
  // An empty list is "any section" only for an any-section filter; otherwise nothing resolved
  const sectionsLabel = sections.length || p.anySection ? sectionsText(sections) : 'no section matched';
  const methods = methodText(p.resolution);
  const notes = noteLines(p, nameOf);
  const specificity = specificityText(p);
  const oursTitle = specificity ? `${OURS_WINS_TITLE} · ${specificity}` : OURS_TITLE;
  const label = filterLabelText(p);
  return (
    <tr className={checked ? undefined : 'off'}>
      <td>
        <input
          type="checkbox"
          checked={checked}
          disabled={!canTick || locked}
          onChange={() => onToggle(proposalKey(p))}
          aria-label={`Import filter ${label}`}
        />
      </td>
      <td className="num">{label}</td>
      <td><span className={`la-run-chip ${chip.kind}`} title={ACTION_TITLES[p.action]}>{chip.text}</span></td>
      <td className="la-import-name" title={p.name}>{p.name}</td>
      <td title={sections.length ? sectionsText(sections, Infinity) : undefined}>
        {sectionsLabel}
        {unresolved.length > 0 && (
          <span className="la-warn-dot" title={`Not matched: ${unresolved.join(', ')}`}>!</span>
        )}
        {methods && <div className="la-hint">{methods}</div>}
      </td>
      <td title={rows.length ? rows.join(', ') : undefined}>{rowsText(rows)}</td>
      <td>{qtyText(p.derived?.minQuantity)}</td>
      <td className="num" title={oursTitle}>{oursText(p)}</td>
      <td>{!!p.autoEnabled && <span className="la-auto-chip" title="This group is on Auto">Auto</span>}</td>
      <td>
        {notes.map((line, i) => (
          <div key={`${i}:${line.text}`} className={line.warn ? 'la-hint warn' : 'la-hint'} title={line.title}>
            {line.text}
          </div>
        ))}
      </td>
    </tr>
  );
}

function LaCheckerImportModal({ eventId, vividUrl = null, groups = [], onClose }) {
  const titleId = useId();
  const queryClient = useQueryClient();
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  // null = the default ticks (defaultSelection), derived from each preview
  const [selectedState, setSelectedState] = useState(null);
  // Also propose the filters that match none of our listings (as creates)
  const [includeEmpty, setIncludeEmpty] = useState(false);
  const { data, isLoading, isError, error, isFetching, refetch } = useLionAutoCheckerPreview(eventId, {
    enabled: !!eventId,
    vividUrl,
    includeEmpty,
  });
  const importM = useImportLionAutoChecker(eventId);

  const busy = importM.isPending;
  const done = importM.isSuccess;
  const locked = busy || done;
  const proposals = Array.isArray(data?.proposals) ? data.proposals : [];
  // The preview's own includeEmpty (the server echoes it): the last answer
  // stays on screen while the box's flip re-reads
  const shownEmpty = data?.includeEmpty ?? includeEmpty;
  const shown = visibleProposals(proposals, shownEmpty);
  const includeLabel = includeEmptyText(proposals, shownEmpty);
  const emptyText = emptyShownText(proposals, shownEmpty);
  const orphans = Array.isArray(data?.orphans) ? data.orphans : [];
  const warnings = Array.isArray(data?.warnings) ? data.warnings : [];
  const selected = selectedState ?? defaultSelection(proposals);
  const tickable = selectableKeys(proposals);
  const tickableSet = new Set(tickable);
  const everyTicked = allSelected(selected, proposals);
  const selections = importSelections(selected, proposals);
  const nameById = new Map((Array.isArray(groups) ? groups : []).map(g => [String(g.id), g.name]));
  const nameOf = (id, fallback) => nameById.get(String(id)) || fallback;

  const handleClose = () => {
    if (!busy) onClose();
  };
  closeRef.current = handleClose;

  // Escape closes (not while an import is in flight)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      closeRef.current?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Focus the dialog on open; give it back to the opener on close
  useEffect(() => {
    const opener = document.activeElement;
    dialogRef.current?.focus();
    return () => {
      if (opener && opener.isConnected && typeof opener.focus === 'function') opener.focus();
    };
  }, []);

  const handleToggle = (key) => setSelectedState(prev => toggleSelection(prev ?? selected, key));
  const handleToggleAll = () => setSelectedState(everyTicked ? new Set() : new Set(tickable));
  // A new preview (with or without the empty filters): its defaults tick afresh
  const handleIncludeEmpty = (e) => {
    setIncludeEmpty(e.target.checked);
    setSelectedState(null);
  };

  // The pricer's "did it go through?": a fresh preview, and the groups and
  // matches behind the modal re-read too (the import hook already does on a
  // 409, a 500 or no answer; this covers any other way they went stale)
  const handleReload = () => {
    setSelectedState(null);
    importM.reset();
    refetch();
    queryClient.invalidateQueries({ queryKey: lionAutoKeys.groups(eventId) });
    queryClient.invalidateQueries({ queryKey: lionAutoKeys.matches(eventId) });
  };

  const handleImport = () => {
    if (done) {
      onClose();
      return;
    }
    if (locked || isFetching || !selections.length) return;
    const confirmText = confirmImportText(selected, proposals, nameOf);
    if (confirmText && !window.confirm(confirmText)) return;
    importM.mutate({ selections, vividUrl, includeEmpty });
  };

  const retryButton = (
    <button type="button" className="btn btn-secondary btn-sm la-status-action" onClick={() => refetch()} disabled={isFetching}>
      {isFetching ? 'Retrying…' : 'Retry'}
    </button>
  );

  const includeLine = (label, disabled) => (
    <div className="la-import-warnings">
      <label className="la-hint">
        <input type="checkbox" checked={includeEmpty} disabled={disabled} onChange={handleIncludeEmpty} />
        {' '}{label}
      </label>
    </div>
  );

  // One line per joined show, primary first ("Lion Checker show" when the
  // preview names none); "Refreshing…" on the first
  const shows = joinedShows(data);
  const showTexts = showLinesText(data);
  const showTitle = data?.vividProductionId ? `VividSeats production ${data.vividProductionId}` : undefined;
  const showLine = data && (shows.length ? shows : [null]).map((show, i) => (
    <div key={`${show?.checkerEventId ?? ''}:${i}`} className="la-import-show" title={showTitle}>
      <strong>{showTexts[i] || 'Lion Checker show'}</strong>
      {!!show?.archived && <span className="la-reason-chip" title="Archived on Lion Checker">archived</span>}
      {i === 0 && isFetching && <span className="la-hint">Refreshing…</span>}
    </div>
  ));

  const orphanBlock = orphans.length > 0 && (
    <div className="la-import-warnings">
      <span className="la-muted">No longer on Lion Checker (kept):</span>
      {orphans.map(o => (
        <span key={o.groupId} className="la-hint">{orphanText(o, nameOf)}</span>
      ))}
    </div>
  );

  let body;
  if (isLoading) {
    body = <div className="la-empty">Reading this show&apos;s filters from Lion Checker…</div>;
  } else if (isError && !data) {
    const text = checkerErrorText(error, { read: true });
    const status = checkerPreviewRetryable(error)
      ? <div className="la-status error">{text}{retryButton}</div>
      : <div className="la-status">{text}</div>;
    body = (
      <>
        {status}
        {includeEmpty && includeLine(INCLUDE_EMPTY_LABEL, isFetching)}
      </>
    );
  } else if (!data) {
    body = <div className="la-empty">No event to read.</div>;
  } else if (!proposals.length) {
    body = (
      <>
        {showLine}
        <div className="la-empty">This show has no filters on Lion Checker yet. Add them there, then open this again.</div>
        {orphanBlock}
      </>
    );
  } else {
    body = (
      <>
        {showLine}
        {isError && (
          <div className="la-status error">
            Could not refresh the preview (showing the last one): {checkerErrorText(error, { read: true })}
            {retryButton}
          </div>
        )}
        {emptyText ? <div className="la-empty">{emptyText}</div> : (
          <table className="la-import-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={everyTicked}
                    disabled={locked || !tickable.length}
                    onChange={handleToggleAll}
                    aria-label="Tick every filter that can be imported"
                  />
                </th>
                <th className="num">#</th>
                <th>Action</th>
                <th>Name</th>
                <th>Sections</th>
                <th>Rows</th>
                <th>Qty</th>
                <th className="num">Ours</th>
                <th>Auto</th>
                <th>Note</th>
              </tr>
            </thead>
            <tbody>
              {shown.map(p => {
                const key = proposalKey(p);
                return (
                  <ProposalRow
                    key={key}
                    p={p}
                    checked={tickableSet.has(key) && selected.has(key)}
                    canTick={tickableSet.has(key)}
                    locked={locked}
                    onToggle={handleToggle}
                    nameOf={nameOf}
                  />
                );
              })}
            </tbody>
          </table>
        )}
        {includeLabel && includeLine(includeLabel, locked)}
        {warnings.length > 0 && (
          <div className="la-import-warnings">
            {warnings.map((w, i) => <span key={`${i}:${w}`} className="la-hint warn">{String(w)}</span>)}
          </div>
        )}
        {orphanBlock}
        <div className="la-import-warnings"><span className="la-hint">{HINT}</span></div>
      </>
    );
  }

  let importLabel = `Import ${selections.length}`;
  if (busy) importLabel = 'Importing…';
  else if (done) importLabel = 'Done';

  return (
    <div className="modal-overlay" onClick={busy ? undefined : handleClose}>
      <div
        ref={dialogRef}
        className="modal modal-content"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        style={{ maxWidth: 980, width: '95%' }}
        onClick={e => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 id={titleId} style={{ margin: 0 }}>Import from Lion Checker</h2>
          <button type="button" className="modal-close" onClick={handleClose} disabled={busy} aria-label="Close">×</button>
        </div>
        <div className="modal-body" style={{ maxHeight: '65vh', minHeight: 160, overflow: 'auto' }}>
          {body}
        </div>
        <div className="modal-footer bulk-review-footer">
          <span className="ai-admin-note" aria-live="polite">
            {done ? importResultText(importM.data) : `${selections.length} selected`}
          </span>
          {importM.isError && (
            <span className="ai-inline-error" aria-live="polite">{checkerErrorText(importM.error)}</span>
          )}
          {importM.isError && importNeedsReload(importM.error) && (
            <button type="button" className="btn-secondary btn-sm" onClick={handleReload} disabled={isFetching}>
              Reload preview
            </button>
          )}
          <span style={{ flex: 1 }} />
          <button type="button" className="btn-secondary" onClick={handleClose} disabled={busy}>Close</button>
          {data && (
            <button
              type="button"
              className="btn-primary"
              onClick={handleImport}
              disabled={!done && (busy || isFetching || !selections.length)}
            >
              {importLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default LaCheckerImportModal;
